<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    text: string
    placement?: 'top' | 'bottom'
  }>(),
  {
    placement: 'top',
  },
)

const hostRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const tipStyle = ref<{ top: string; left: string; transform?: string }>({
  top: '0px',
  left: '0px',
})

const tipText = computed(() => (props.text ?? '').trim())
const showTip = computed(() => visible.value && tipText.value.length > 0)

function updatePosition() {
  const el = hostRef.value
  if (!el) return
  const anchor = (el.firstElementChild as HTMLElement | null) ?? el
  const r = anchor.getBoundingClientRect()
  const textAlign = window.getComputedStyle(anchor).textAlign
  const gap = 8
  const below = props.placement === 'bottom'
  let left = r.left
  let transform = below ? 'translateY(0)' : 'translateY(-100%)'

  if (textAlign === 'center') {
    left = r.left + r.width / 2
    transform = below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)'
  } else if (textAlign === 'right' || textAlign === 'end') {
    left = r.right
    transform = below ? 'translate(-100%, 0)' : 'translate(-100%, -100%)'
  }

  tipStyle.value = {
    top: below ? `${r.bottom + gap}px` : `${r.top - gap}px`,
    left: `${left}px`,
    transform,
  }
}

function show() {
  updatePosition()
  visible.value = true
}

function hide() {
  visible.value = false
}

function onScrollOrResize() {
  if (visible.value) updatePosition()
}

onMounted(() => {
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
})
</script>

<template>
  <span
    ref="hostRef"
    class="assign-name-tip-host"
    @mouseenter="show"
    @mouseleave="hide"
    @focusin="show"
    @focusout="hide"
  >
    <slot />
    <Teleport to="body">
      <div v-show="showTip" class="assign-name-tip" role="tooltip" :style="tipStyle">
        {{ tipText }}
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.assign-name-tip-host {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.assign-name-tip {
  position: fixed;
  z-index: 13000;
  box-sizing: border-box;
  max-width: 320px;
  padding: 8px 12px;
  background: #fef39c;
  border-radius: 8px;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #484848;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  pointer-events: none;
}
</style>
